import { useCallback, useState } from 'react'

import { Box, Button, makeStyles, TextField, CircularProgress } from '@material-ui/core'
import { logToAnalytics } from 'modules'
import { BillingPeriod } from '@stigg/js-client-sdk'

const useStyles = makeStyles(theme => ({
    infoButton: {
        position: 'relative',
        color: '#26c6da',
        fontWeight: 500,
        padding: '0',
        margin: '0',
        lineHeight: 'normal',
        background: 'none !important',
        minWidth: 'auto',
        marginLeft: 'auto'
    },
    promoLoaderWrapper: {
        top: '50%',
        right: '100%',
        position: 'absolute',
        transform: 'translateY(-50%)',
        marginRight: theme.spacing(1),
        pointerEvents: 'none'
    }
}))

type Props = {
    onReqStart?: () => void
    onReqEnd?: () => void
    onSuccess?: (data: CouponResDataType) => void
    planId: string
    billingPeriod: BillingPeriod
    unitQuantity: number
    applyCoupon: (input: ApplyCouponProps) => Promise<CouponResDataType>
    promoCode: string
    isCouponApplying: boolean
}

export type ApplyCouponProps = {
    couponId: string | null
    planId?: string
    billingPeriod?: BillingPeriod
    unitQuantity?: number
}

export type CouponResDataType =
    | {
          couponId: string
      }
    | undefined

export const ApplyCoupon = ({
    onReqStart,
    onReqEnd,
    onSuccess,
    billingPeriod,
    planId,
    unitQuantity,
    applyCoupon,
    promoCode,
    isCouponApplying
}: Props) => {
    const classes = useStyles()

    const [newPromoCode, setNewPromoCode] = useState(promoCode)
    const [errorMessage, setErrorMessage] = useState('')

    const applyCouponCallback = useCallback(
        (props: ApplyCouponProps) => {
            const eventName = props.couponId ? 'apply-coupon' : 'remove-coupon'

            onReqStart?.()
            applyCoupon(props)
                .then(res => {
                    logToAnalytics(eventName, {
                        status: 'successful',
                        ...props
                    })
                    onSuccess?.(res)
                })
                .catch((e: Error) => {
                    logToAnalytics(eventName, {
                        message: e.message,
                        status: 'failed',
                        ...props
                    })
                    setErrorMessage(e.message || 'This code is invalid')
                })
                .finally(() => onReqEnd?.())
        },
        [applyCoupon, onReqEnd, onReqStart, onSuccess]
    )

    return (
        <Box display="flex" alignItems="baseline">
            <TextField
                fullWidth
                style={{ marginRight: 'auto', maxWidth: '50%' }}
                value={newPromoCode}
                InputProps={{
                    readOnly: Boolean(promoCode),
                    placeholder: 'Add promotion code',
                    style: {
                        fontSize: '14px',
                        lineHeight: 'normal'
                    }
                }}
                onChange={e => {
                    setErrorMessage('')
                    setNewPromoCode(e.target.value.toUpperCase())
                }}
                {...(errorMessage && {
                    error: true,
                    helperText: errorMessage
                })}
            />

            <Button
                className={classes.infoButton}
                variant="text"
                disableRipple
                disabled={isCouponApplying || !newPromoCode}
                onClick={() => {
                    const updateProps = promoCode
                        ? { couponId: '' }
                        : { couponId: newPromoCode, planId, billingPeriod, unitQuantity }

                    applyCouponCallback(updateProps)
                }}
            >
                {isCouponApplying && (
                    <Box className={classes.promoLoaderWrapper}>
                        <CircularProgress size={16} />
                    </Box>
                )}
                {promoCode ? 'Remove' : 'Apply'}
            </Button>
        </Box>
    )
}
